import { __ } from "@wordpress/i18n";
import { useState, useRef, useEffect, createPortal } from "@wordpress/element";
import {
    MoreHorizontal,
    Edit3,
    Trash2,
    Eye,
    Copy,
    CopyPlus,
} from "lucide-react";
import { useRulesContext } from "../../../../../hook/useRulesContext";
import { useConfirm } from "../../../../../hook/useConfirm";

export default function ActionDropdown({ item }) {
    const {
        trashShortcode,
        deleteShortcode,
        duplicateShortcode,
        showEditModal,
    } = useRulesContext();
    const { confirm, ConfirmDialog } = useConfirm();
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, right: 0 });
    const buttonRef = useRef(null);

    const calculatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();

            setPosition({
                top: rect.bottom + 4, // Just use viewport position directly
                right: window.innerWidth - rect.right,
            });
        }
    };

    useEffect(() => {
        if (isOpen) {
            // Small delay to ensure DOM is ready
            requestAnimationFrame(() => {
                calculatePosition();
            });
        }
    }, [isOpen]);

    // Update position on scroll or resize
    useEffect(() => {
        if (!isOpen) return;

        window.addEventListener("resize", calculatePosition);
        window.addEventListener("scroll", calculatePosition, true);

        return () => {
            window.removeEventListener("resize", calculatePosition);
            window.removeEventListener("scroll", calculatePosition, true);
        };
    }, [isOpen]);

    const handleTrash = async () => {
        const confirmed = await confirm(
            __(
                "Are you sure you want to move this rule to trash?",
                "wpbean-quantity-controls-for-woocommerce",
            ),
            {
                title: __(
                    "Move to Trash?",
                    "wpbean-quantity-controls-for-woocommerce",
                ),
                type: "danger",
                btnText: __(
                    "Yes, Trash It",
                    "wpbean-quantity-controls-for-woocommerce",
                ),
            },
        );

        if (confirmed) {
            trashShortcode(item.id);
        }

        setIsOpen(false);
    };

    const handleDelete = async () => {
        const deleteConfirmed = await confirm(
            __(
                "Are you sure you want to delete this rule? This action cannot be undone.",
                "wpbean-quantity-controls-for-woocommerce",
            ),
            {
                title: __(
                    "Delete Rule?",
                    "wpbean-quantity-controls-for-woocommerce",
                ),
                type: "danger",
                btnText: __(
                    "Yes, Delete It",
                    "wpbean-quantity-controls-for-woocommerce",
                ),
            },
        );

        if (deleteConfirmed) {
            deleteShortcode(item.id);
        }
        setIsOpen(false);
    };

    const handleEditShortCode = () => {
        showEditModal(item.id);
        setIsOpen(false);
    };

    const handleDuplicate = async () => {
        const confirmed = await confirm(
            "Are you sure you want to duplicate this rule?",
            { title: "Duplicate Item?" },
        );

        if (confirmed) {
            duplicateShortcode(item.id);
        }
        setIsOpen(false);
    };

    // Get the portal container
    const portalContainer = document.querySelector(".wpb-pmqb-app-wrapper");

    const dropdownContent = isOpen && (
        <>
            <div
                className="fixed inset-0 z-10"
                onClick={() => setIsOpen(false)}
            />
            <div
                className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
                style={{
                    top: `${position.top}px`,
                    right: `${position.right}px`,
                }}
            >
                <button
                    onClick={handleEditShortCode}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                    <Edit3 className="w-4 h-4 mr-3" />
                    {__("Edit", "wpbean-quantity-controls-for-woocommerce")}
                </button>
                <button
                    onClick={handleDuplicate}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                    <CopyPlus className="w-4 h-4 mr-3" />

                    {__(
                        "Duplicate",
                        "wpbean-quantity-controls-for-woocommerce",
                    )}
                </button>
                <hr className="my-1" />

                {item.rule_status !== "trash" && (
                    <button
                        onClick={handleTrash}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                        <Trash2 className="w-4 h-4 mr-3" />
                        {__(
                            "Move to Trash",
                            "wpbean-quantity-controls-for-woocommerce",
                        )}
                    </button>
                )}

                {item.rule_status === "trash" && (
                    <button
                        onClick={handleDelete}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                        <Trash2 className="w-4 h-4 mr-3" />
                        {__(
                            "Delete",
                            "wpbean-quantity-controls-for-woocommerce",
                        )}
                    </button>
                )}
            </div>
        </>
    );

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
                <MoreHorizontal className="w-4 h-4" />
            </button>
            {isOpen &&
                portalContainer &&
                createPortal(dropdownContent, portalContainer)}
            <ConfirmDialog />
        </div>
    );
}
